import React from 'react';
import { Sparkles, ArrowRight, Eye, EyeOff, Smartphone } from 'lucide-react';
import { resolveMediaUrl, handleImageError } from '../../../utils/mediaResolver';

export default function HomepageSectionPreview({ form = {} }) {
  const sectionType = form.section_type || 'product';
  const isEnabled = form.enabled !== false;

  return (
    <div className="bg-slate-50 min-h-full flex flex-col font-sans">
      {/* Top Bar */}
      <div className="bg-white border-b px-4 py-2.5 flex flex-wrap items-center justify-between gap-2 text-xs">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-slate-500">Section Type:</span>
          <span className="px-2 py-0.5 rounded bg-indigo-100 text-indigo-800 font-bold uppercase text-[11px]">
            {sectionType}
          </span>
          <span className="font-mono text-slate-400 text-[11px]">Key: {form.section_key || 'unassigned'}</span>
        </div>

        <div className="flex items-center gap-2">
          <span
            className={`px-2 py-0.5 rounded-full font-bold text-[10px] uppercase flex items-center gap-1 ${
              isEnabled ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'
            }`}
          >
            {isEnabled ? <><Eye className="w-3 h-3" /> Visible on Homepage</> : <><EyeOff className="w-3 h-3" /> Hidden from Homepage</>}
          </span>
          <span className="text-slate-400 text-[11px]">Order: #{form.display_order ?? 0}</span>
        </div>
      </div>

      {/* Section Mockup Canvas */}
      <div className="p-6 md:p-8 flex-1 flex flex-col justify-center">
        <div className="max-w-4xl mx-auto w-full bg-white rounded-2xl border border-slate-200 shadow-md p-6 md:p-8 space-y-6">
          {/* Header */}
          <div className="flex flex-wrap items-end justify-between gap-4 border-b pb-4">
            <div>
              {form.eyebrow && (
                <span className="text-[11px] font-bold uppercase tracking-wider text-[#2B3380] block mb-1">
                  {form.eyebrow}
                </span>
              )}
              <h2 className="text-xl md:text-2xl font-extrabold text-gray-900">
                {form.title || <span className="text-gray-300 italic">Section Title</span>}
              </h2>
              {form.description && (
                <p className="text-xs text-gray-500 mt-1 max-w-xl">{form.description}</p>
              )}
            </div>

            {(form.cta_label || form.cta_href) && (
              <div>
                <span className="text-xs font-bold text-[#2B3380] flex items-center gap-1 hover:underline">
                  {form.cta_label || 'View all'} <ArrowRight className="w-3.5 h-3.5" />
                </span>
              </div>
            )}
          </div>

          {/* Section Body based on type */}
          {sectionType === 'product' && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <span>Filter Category:</span>
                <span className="px-2 py-0.5 rounded bg-slate-100 font-semibold text-slate-800">
                  {form.category || 'All Devices'}
                </span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="border rounded-xl p-3 bg-slate-50 flex flex-col justify-between h-44 text-xs">
                    <div className="h-24 bg-white rounded-lg flex items-center justify-center text-slate-300 border text-[11px]">
                      {form.category || 'Device'} Card #{i}
                    </div>
                    <div>
                      <div className="font-semibold text-slate-800 truncate">Sample {form.category || 'Refurbished'} Phone</div>
                      <div className="font-bold text-green-700 mt-0.5">₹14,999</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {sectionType === 'categories' && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {['Mobiles', 'Laptops', 'Tablets', 'Accessories'].map((cat, i) => (
                <div key={i} className="border rounded-xl p-4 bg-slate-50 text-center hover:bg-slate-100 transition">
                  <div className="w-12 h-12 rounded-full bg-white mx-auto flex items-center justify-center text-lg mb-2 shadow-sm border">
                    📱
                  </div>
                  <strong className="text-xs text-slate-800 block">{cat}</strong>
                </div>
              ))}
            </div>
          )}

          {sectionType === 'brands' && (
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 items-center">
              {['Apple', 'Samsung', 'OnePlus', 'Xiaomi', 'Vivo', 'Dell'].map((brand, i) => (
                <div key={i} className="border rounded-xl p-3 bg-slate-50 text-center font-bold text-slate-700 text-xs">
                  {brand}
                </div>
              ))}
            </div>
          )}

          {sectionType === 'testimonials' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[1, 2].map((i) => (
                <div key={i} className="border rounded-xl p-4 bg-slate-50/70 space-y-2 text-xs">
                  <div className="text-amber-500 font-bold">★★★★★</div>
                  <p className="text-slate-600 italic">"Zobox made it possible for me to get a verified refurbished device in mint condition."</p>
                  <div className="font-bold text-slate-900">— Verified Customer #{i}</div>
                </div>
              ))}
            </div>
          )}

          {sectionType === 'stores' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {['Karol Bagh Store, Delhi', 'Sector 63 Store, Noida'].map((store, i) => (
                <div key={i} className="border rounded-xl p-4 bg-slate-50 flex justify-between items-center text-xs">
                  <div>
                    <strong className="text-slate-900 block">{store}</strong>
                    <span className="text-slate-500">Open 10 AM - 8 PM</span>
                  </div>
                  <span className="text-[#2B3380] font-bold">Get Directions →</span>
                </div>
              ))}
            </div>
          )}

          {sectionType === 'articles' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="border rounded-xl overflow-hidden bg-slate-50 text-xs">
                  <div className="h-20 bg-slate-200 flex items-center justify-center text-slate-400">Article Image</div>
                  <div className="p-3">
                    <strong className="text-slate-900 block">ZOBOX in the news: Refurbished device revolution #{i}</strong>
                    <span className="text-slate-500 mt-1 block">Read full coverage →</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {sectionType === 'callout' && (
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 p-6 rounded-xl flex flex-wrap items-center justify-between gap-4">
              <div className="space-y-1 max-w-lg">
                <h3 className="text-lg font-bold text-blue-950">{form.title || 'Callout Banner'}</h3>
                <p className="text-xs text-blue-800">{form.description || 'Promotional announcement or callout body text.'}</p>
              </div>
              <div className="flex flex-wrap gap-2">
                {form.cta_label && (
                  <span className="px-4 py-2 rounded-lg bg-[#2B3380] text-white text-xs font-bold shadow-sm">
                    {form.cta_label}
                  </span>
                )}
                {form.secondary_label && (
                  <span className="px-4 py-2 rounded-lg bg-white border border-slate-300 text-slate-700 text-xs font-semibold">
                    {form.secondary_label}
                  </span>
                )}
              </div>
            </div>
          )}

          {sectionType === 'app-download' && (
            <div className="bg-slate-900 text-white rounded-xl p-6 flex flex-wrap items-center justify-between gap-6">
              <div className="space-y-2 max-w-md">
                <h3 className="text-lg font-bold">{form.title || 'Download the ZOBIZ app'}</h3>
                <p className="text-xs text-slate-300">
                  {form.description || 'Available for Android and iOS devices. Wholesale pricing on the go.'}
                </p>
                <div className="flex flex-wrap gap-3 pt-2">
                  <div className="px-3 py-1.5 bg-slate-800 rounded-lg border border-slate-700 text-xs flex items-center gap-2">
                    {form.primary_button_image ? (
                      <img src={resolveMediaUrl(form.primary_button_image)} alt="Store" className="h-6" onError={handleImageError} />
                    ) : (
                      <span>Google Play</span>
                    )}
                  </div>
                  <div className="px-3 py-1.5 bg-slate-800 rounded-lg border border-slate-700 text-xs flex items-center gap-2">
                    {form.secondary_button_image ? (
                      <img src={resolveMediaUrl(form.secondary_button_image)} alt="Store" className="h-6" onError={handleImageError} />
                    ) : (
                      <span>App Store</span>
                    )}
                  </div>
                </div>
              </div>
              {form.image && (
                <img src={resolveMediaUrl(form.image)} alt="" className="h-28 object-contain" onError={handleImageError} />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
